import React, { useEffect } from 'react';
import { atom, selector, useRecoilValue, useSetRecoilState } from 'recoil';

import { useEventCallback } from '@gitbook/util-react-hooks';

import { persistRecoilEffect } from '../../utils';

export const desktopSidebarWidthAtom = atom<number | null>({
    key: 'sidebarWidth',
    default: null,
    effects: [persistRecoilEffect()],
});

export const desktopSidebarCollapsedAtom = atom<boolean>({
    key: 'sidebarCollapsed',
    default: false,
    effects: [persistRecoilEffect()],
});

/**
 * Whether the sidebar is locked as closed.
 * The set contains callback to be called when unlocking.
 */
export const sidebarLockedAtom = atom<Set<() => void>>({
    key: 'sidebarLockedAtom',
    default: new Set<() => void>(),
});

/**
 * Selector to know when the sidebar is locked or not.
 */
const sidebarIsLockedSelector = selector({
    key: 'sidebarIsLockedSelector',
    get: ({ get }) => {
        return get(sidebarLockedAtom).size > 0;
    },
});

/**
 * Lock the sidebar in closed position and be notified when the sidebar is unlocked by the user
 */
export function useLockSidebar(locked: boolean, onUnlock: () => void = () => {}) {
    const setLocked = useSetRecoilState(sidebarLockedAtom);
    const onUnlockEvent = useEventCallback(onUnlock);

    useEffect(() => {
        if (!locked) {
            return;
        }

        setLocked((prev) => {
            const updated = new Set(prev);
            updated.add(onUnlockEvent);
            return updated;
        });

        return () => {
            setLocked((prev) => {
                const updated = new Set(prev);
                updated.delete(onUnlockEvent);
                return updated;
            });
        };
    }, [onUnlockEvent, locked, setLocked]);
}

/**
 * Unlock and opens the sidebar. The function should be called as the result of a user action.
 */
export function useUnlockSidebar() {
    const setLocked = useSetRecoilState(sidebarLockedAtom);
    return React.useCallback(() => {
        let cleanupCallbacks: (() => void)[] = [];

        setLocked((prev) => {
            cleanupCallbacks = Array.from(prev);
            return new Set();
        });

        cleanupCallbacks.forEach((cb) => cb());
    }, [setLocked]);
}

/**
 * Hook to get the open state of a content in the sidebar.
 */
export function useIsSidebarLocked(): boolean {
    return useRecoilValue(sidebarIsLockedSelector);
}
